<template>
    <v-modal
        scroll
        size="large"
        title="Test Webhook"
        @close="closeModal">
        <div slot="contents">
            <div class="modal-content">
                <p>
                    Please note that the same credentials and server information will be used. Optionally you can modify
                    placeholders with your own data or leave them as-is.
                </p>
            </div>
            <preview-webhook-placeholders
                    :has-placeholders="hasUrlPlaceholders"
                    :placeholders="placeholders.url"
                    @input="value => updatePlaceholder('url', value.token, value.value)"
            >Placeholders for the webhook url</preview-webhook-placeholders>
            <preview-webhook-placeholders
                    :has-placeholders="hasBodyPlaceholders"
                    :placeholders="placeholders.body"
                    @input="value => updatePlaceholder('body', value.token, value.value)"
            >Placeholders for the webhook body</preview-webhook-placeholders>
            <preview-webhook-placeholders
                :has-placeholders="hasHeadersPlaceholders"
                :placeholders="placeholders.headers"
                @input="value => updatePlaceholder('headers', value.token, value.value)"
            >Placeholders for the webhook headers</preview-webhook-placeholders>
            <div class="modal-content">
                <h3 class="stack-m">Webhook Content Preview</h3>
                <p class="stack-m">Below you can preview how the webhook body will look when received by the remote
                    server. Any modifications to the placeholders above will be reflected automatically below.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-full">
                        <r-input disabled
                                 label="Webhook URL"
                                 :value="webhookUrl"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Webhook Body"
                            :value="webhookBody"
                            :multiline="true"
                            rows="5"
                            disabled/>
                    </div>
                    <div class="grid-item" v-if="hasWebhooks">
                        <span class="field-label">Webhook Headers</span>
                        <ul class="list list-group">
                            <li class="list-item align-items-center" v-for="header in webhookHeaders" :key="header.name">
                                <div class="field list-item-content inline-m">
                                    <r-input :value="header.name" disabled/>
                                </div>
                                <div class="field list-item-content inline-m">
                                    <r-input :value="header.value" disabled/>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <template slot="actions">
            <r-button
                :type="isLoading ? 'disabled' : 'primary'"
                :disabled="isLoading"
                @click="handleSendWebhook">Send Webhook
            </r-button>
        </template>
    </v-modal>
</template>

<script>
    import matchAll from '@/common/helpers/match-all';
    import _ from '@/common/helpers';
    import vModal from '@/components/modal/v-modal';
    import placeholderPattern from '@/components/webhook/placeholder-pattern';
    import PreviewWebhookPlaceholders from './preview-webhook-placeholders';

    export default {
        name: 'preview-webhook-view',
        components: {
            vModal,
            PreviewWebhookPlaceholders,
        },
        props: {
            isLoading: {
                type: Boolean,
                required: true,
            },
            /**
             * @type {WebhookModel|{}}
             */
            webhook: Object,
        },
        data() {
            return {
                placeholders: {
                    url: this.makePlaceholders(this.webhook.url),
                    body: this.makePlaceholders(this.webhook.body),
                    headers: this.makePlaceholders(
                        this.webhook.headers.reduce((accumulator, value) => `${accumulator}${value.value}`, ''),
                    ),
                },
            };
        },
        computed: {
            hasUrlPlaceholders() {
                return !!this.placeholders.url.length;
            },
            hasBodyPlaceholders() {
                return !!this.placeholders.body.length;
            },
            hasHeadersPlaceholders() {
                return !!this.placeholders.headers.length;
            },
            hasWebhooks() {
                return !!this.webhook.headers.length;
            },
            webhookUrl() {
                return this.placeholders.url
                    .reduce(
                        (memo, item) => memo.replace(item.token, item.value),
                        this.webhook.url,
                    );
            },
            webhookBody() {
                return this.placeholders.body
                    .reduce(
                        (memo, item) => memo.replace(item.token, item.value),
                        this.webhook.body,
                    );
            },
            webhookHeaders() {
                return this.webhook.headers.map(header => ({
                    name: header.name,
                    value: this.placeholders.headers.reduce(
                        (memo, item) => memo.replace(item.token, item.value),
                        header.value,
                    ),
                }));
            },
        },
        methods: {
            makePlaceholders(target) {
                const matches = matchAll(target, placeholderPattern);

                // only unique tokens
                const keys = matches.reduce((memo, match) => ({
                    ...memo,
                    [match[1]]: match,
                }), {});

                return Object.values(keys).map(match => ({
                    value: match[0],
                    token: match[0],
                    label: match[1].split('.').map(_.capitalize).join(' '),
                }));
            },
            closeModal() {
                this.$emit('cancel');
            },
            handleSendWebhook() {
                this.$emit('test-webhook', {
                    url: this.webhookUrl,
                    body: this.webhookBody,
                });
            },
            updatePlaceholder(type, token, placeholder) {
                const item = this.placeholders[type].find(data => data.token === token);
                item.value = placeholder;
            },
        },
    };
</script>
